(() => {
  function registerMediaFeatures() {
    const theme = window.KSTheme;

    if (!theme?.registerFeature) return;

    const { collect } = theme;
    const body = document.body;
    const desktopHorizontalScrollMedia = window.matchMedia('(min-width: 768px)');
    const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

  function getSurfaceVideos(surface) {
    return Array.from(surface.querySelectorAll('video'));
  }

  function getVisibleSurfaceVideo(surface) {
    const videos = getSurfaceVideos(surface);

    return videos.find((video) => video.offsetParent !== null) || videos[0] || null;
  }

  function syncMediaMuteUi(surface) {
    const muted = surface.dataset.videoMuted !== 'false';

    collect(surface, '[data-media-mute-toggle]').forEach((button) => {
      const label = button.querySelector('[data-media-mute-label]');
      const nextText = muted ? button.dataset.labelUnmute : button.dataset.labelMute;

      button.setAttribute('aria-pressed', muted ? 'false' : 'true');

      if (label) {
        label.textContent = nextText || '';
      } else if (nextText) {
        button.textContent = nextText;
      }
    });
  }

  function applySurfaceMute(surface, muted) {
    surface.dataset.videoMuted = muted ? 'true' : 'false';

    getSurfaceVideos(surface).forEach((video) => {
      video.muted = muted;
      video.defaultMuted = muted;
    });

    syncMediaMuteUi(surface);
  }

  function pauseSurfaceVideos(surface) {
    getSurfaceVideos(surface).forEach((video) => {
      video.pause();
    });
  }

  function playSurfaceVideo(surface) {
    if (surface.dataset.autoplayVideo !== 'true') return;

    const video = getVisibleSurfaceVideo(surface);

    if (!video) return;

    video.play().catch(() => {});
  }

  function initMediaSurfaces(scope = document) {
    collect(scope, '[data-media-surface]').forEach((surface) => {
      if (surface.dataset.mediaSurfaceInitialized === 'true') return;
      surface.dataset.mediaSurfaceInitialized = 'true';

      applySurfaceMute(surface, surface.dataset.videoMuted !== 'false');

      collect(surface, '[data-media-mute-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          applySurfaceMute(surface, surface.dataset.videoMuted === 'false');
        });
      });

      if (!surface.closest('[data-slideshow]')) {
        playSurfaceVideo(surface);
      }
    });
  }

  function initSlideshows(scope = document) {
    collect(scope, '[data-slideshow]').forEach((slideshow) => {
      if (slideshow.dataset.slideshowInitialized === 'true') return;
      slideshow.dataset.slideshowInitialized = 'true';

      const slides = Array.from(slideshow.querySelectorAll('[data-slide]'));
      const dots = Array.from(slideshow.querySelectorAll('[data-slideshow-dot]'));
      const prevButton = slideshow.querySelector('[data-slideshow-prev]');
      const nextButton = slideshow.querySelector('[data-slideshow-next]');
      const toggleButton = slideshow.querySelector('[data-slideshow-toggle]');
      const autoplayEnabled = slideshow.dataset.autoplay === 'true';
      const interval = Number(slideshow.dataset.interval || 6000);
      const isAnnouncementSlideshow = slideshow.classList.contains('announcement-bar__viewport');
      const pauseLabel = String(slideshow.dataset.slideshowPauseLabel || 'Pause rotation').trim();
      const playLabel = String(slideshow.dataset.slideshowPlayLabel || 'Resume rotation').trim();
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let pausedByUser = false;
      let suspended = false;
      let timer = null;
      let transitionTimer = null;

      function stopAutoplay() {
        window.clearInterval(timer);
        timer = null;
      }

      function updateToggleButton() {
        if (!toggleButton) return;

        const isPaused = pausedByUser;
        const toggleText = toggleButton.querySelector('[data-slideshow-toggle-text]');
        const pauseIcon = toggleButton.querySelector('[data-slideshow-toggle-icon="pause"]');
        const playIcon = toggleButton.querySelector('[data-slideshow-toggle-icon="play"]');

        toggleButton.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
        toggleButton.setAttribute('aria-label', isPaused ? playLabel : pauseLabel);

        if (toggleText) {
          toggleText.textContent = isPaused ? 'Play' : 'Pause';
        }

        if (pauseIcon) {
          pauseIcon.hidden = isPaused;
        }

        if (playIcon) {
          playIcon.hidden = !isPaused;
        }
      }

      function syncAutoplay({ userInitiated = false } = {}) {
        stopAutoplay();

        if (!autoplayEnabled || slides.length < 2 || pausedByUser || suspended) return;
        if (reducedMotionMedia.matches && !userInitiated) return;

        timer = window.setInterval(() => {
          setActive(activeIndex + 1);
        }, interval);
      }

      function setActive(index) {
        if (slides.length === 0) return;

        const nextIndex = (index + slides.length) % slides.length;
        const previousSlide = slides[activeIndex];
        const nextSlide = slides[nextIndex];
        const slideDirection = index < activeIndex ? 'previous' : 'next';
        const shouldCrossfade = (
          slideshow.classList.contains('ks-slideshow__viewport')
          && nextIndex !== activeIndex
          && !reducedMotionMedia.matches
        );
        const shouldSlideAnnouncement = (
          isAnnouncementSlideshow
          && nextIndex !== activeIndex
          && !reducedMotionMedia.matches
        );

        window.clearTimeout(transitionTimer);
        slideshow.classList.remove('is-transitioning');
        slideshow.removeAttribute('data-slide-direction');
        slides.forEach((slide) => slide.classList.remove('is-outgoing'));

        if ((shouldCrossfade || shouldSlideAnnouncement) && previousSlide && nextSlide) {
          previousSlide.classList.add('is-outgoing');
        }

        if (shouldSlideAnnouncement) {
          slideshow.dataset.slideDirection = slideDirection;
          slideshow.classList.add('is-transitioning');
        }

        activeIndex = nextIndex;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === activeIndex;
          const surface = slide.querySelector('[data-media-surface]');

          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

          if (surface) {
            if (isActive) {
              playSurfaceVideo(surface);
              syncMediaMuteUi(surface);
            } else {
              pauseSurfaceVideos(surface);
            }
          }
        });

        if (shouldCrossfade && previousSlide) {
          transitionTimer = window.setTimeout(() => {
            previousSlide.classList.remove('is-outgoing');
          }, 620);
        } else if (shouldSlideAnnouncement && previousSlide) {
          transitionTimer = window.setTimeout(() => {
            previousSlide.classList.remove('is-outgoing');
            slideshow.classList.remove('is-transitioning');
            slideshow.removeAttribute('data-slide-direction');
          }, 480);
        }

        dots.forEach((dot, dotIndex) => {
          const isActive = dotIndex === activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          setActive(activeIndex - 1);
          syncAutoplay({ userInitiated: true });
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          setActive(activeIndex + 1);
          syncAutoplay({ userInitiated: true });
        });
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          setActive(Number(dot.dataset.slideIndex || 0));
          syncAutoplay({ userInitiated: true });
        });
      });

      if ((slideshow.classList.contains('ks-slideshow__viewport') || isAnnouncementSlideshow) && slides.length > 1) {
        const mobileSwipeMedia = window.matchMedia('(max-width: 767px)');
        const minimumSwipeDistance = 48;
        let swipePointerId = null;
        let swipeStartX = 0;
        let swipeStartY = 0;
        let suppressSwipeClick = false;

        const resetSwipe = () => {
          swipePointerId = null;
          swipeStartX = 0;
          swipeStartY = 0;
        };

        slideshow.addEventListener('pointerdown', (event) => {
          if (!mobileSwipeMedia.matches || event.pointerType === 'mouse' || !event.isPrimary) return;
          if (event.target.closest('button, input, select, textarea')) return;

          swipePointerId = event.pointerId;
          swipeStartX = event.clientX;
          swipeStartY = event.clientY;
        }, { passive: true });

        slideshow.addEventListener('pointerup', (event) => {
          if (event.pointerId !== swipePointerId) return;

          const distanceX = event.clientX - swipeStartX;
          const distanceY = event.clientY - swipeStartY;
          const isHorizontalSwipe = (
            Math.abs(distanceX) >= minimumSwipeDistance
            && Math.abs(distanceX) > Math.abs(distanceY) * 1.15
          );

          resetSwipe();

          if (!isHorizontalSwipe) return;

          suppressSwipeClick = true;
          setActive(activeIndex + (distanceX < 0 ? 1 : -1));
          syncAutoplay({ userInitiated: true });
          window.setTimeout(() => {
            suppressSwipeClick = false;
          }, 0);
        }, { passive: true });

        slideshow.addEventListener('pointercancel', resetSwipe, { passive: true });

        slideshow.addEventListener('click', (event) => {
          if (!suppressSwipeClick) return;

          event.preventDefault();
          event.stopPropagation();
          suppressSwipeClick = false;
        }, true);
      }

      if (toggleButton) {
        toggleButton.addEventListener('click', () => {
          pausedByUser = !pausedByUser;
          updateToggleButton();
          syncAutoplay({ userInitiated: true });
        });
      }

      slideshow.addEventListener('mouseenter', () => {
        suspended = true;
        syncAutoplay();
      });
      slideshow.addEventListener('mouseleave', () => {
        suspended = false;
        syncAutoplay();
      });
      slideshow.addEventListener('focusin', () => {
        suspended = true;
        syncAutoplay();
      });
      slideshow.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (slideshow.contains(document.activeElement)) return;
          suspended = false;
          syncAutoplay();
        }, 0);
      });

      slideshow.addEventListener('shopify:block:select', (event) => {
        const selectedSlide = event.target.closest('[data-slide]');
        const selectedIndex = slides.indexOf(selectedSlide);
        if (selectedIndex < 0) return;

        suspended = true;
        setActive(selectedIndex);
        syncAutoplay();
      });

      slideshow.addEventListener('shopify:block:deselect', () => {
        suspended = false;
        syncAutoplay();
      });

      setActive(activeIndex);
      updateToggleButton();
      syncAutoplay();
    });
  }

  function initBasicContentFadingSlideshows(scope = document) {
    collect(scope, '[data-basic-content-fading-slideshow]').forEach((slideshow) => {
      if (slideshow.dataset.fadingSlideshowInitialized === 'true') return;

      const slides = collect(slideshow, '[data-fading-slideshow-slide]');
      if (slides.length === 0) return;

      const interval = Math.max(2000, Number(slideshow.dataset.rotationInterval) || 5000);
      const images = collect(slideshow, 'img');
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let timer = null;
      let transitionTimer = null;
      let isVisible = true;
      let mediaReady = images.length === 0;

      function syncAdaptiveAspectRatio() {
        if (!slideshow.classList.contains('basic-content-fading-slideshow--adapt')) return;

        const firstImage = slides[0]?.querySelector('img') || images[0];
        if (!firstImage) return;

        const imageWidth = Number(firstImage.getAttribute('width')) || firstImage.naturalWidth;
        const imageHeight = Number(firstImage.getAttribute('height')) || firstImage.naturalHeight;

        if (imageWidth > 0 && imageHeight > 0) {
          slideshow.style.setProperty(
            '--basic-content-fading-slideshow-aspect-ratio',
            String(imageWidth / imageHeight)
          );
        }
      }

      function setActive(index) {
        const nextIndex = (index + slides.length) % slides.length;
        const previousSlide = slides[activeIndex];
        const nextSlide = slides[nextIndex];
        const shouldAnimate = nextIndex !== activeIndex
          && slideshow.classList.contains('is-initialized')
          && !reducedMotionMedia.matches;

        window.clearTimeout(transitionTimer);
        slides.forEach((slide) => slide.classList.remove('is-outgoing'));

        if (shouldAnimate && previousSlide && nextSlide) {
          slides.forEach((slide, slideIndex) => {
            const isNext = slideIndex === nextIndex;
            slide.classList.toggle('is-active', isNext);
            slide.setAttribute('aria-hidden', isNext ? 'false' : 'true');
          });

          previousSlide.classList.add('is-outgoing');
          activeIndex = nextIndex;
          transitionTimer = window.setTimeout(() => {
            previousSlide.classList.remove('is-outgoing');
          }, 850);
          return;
        }

        activeIndex = nextIndex;
        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
      }

      function stopAutoplay() {
        window.clearInterval(timer);
        timer = null;
      }

      function syncAutoplay() {
        stopAutoplay();
        if (slides.length < 2 || !mediaReady || !isVisible || document.hidden || reducedMotionMedia.matches) return;

        timer = window.setInterval(() => {
          if (!slideshow.isConnected) {
            stopAutoplay();
            return;
          }
          setActive(activeIndex + 1);
        }, interval);
      }

      slideshow.dataset.fadingSlideshowInitialized = 'true';
      syncAdaptiveAspectRatio();
      slideshow.classList.add('is-initialized');
      setActive(0);
      syncAutoplay();

      if (!mediaReady) {
        const decodeImage = (image) => new Promise((resolve) => {
          const finish = () => {
            if (typeof image.decode !== 'function') {
              resolve();
              return;
            }

            image.decode().catch(() => {}).then(resolve);
          };

          if (image.complete) {
            finish();
            return;
          }

          image.addEventListener('load', finish, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });

        Promise.all(images.map(decodeImage)).then(() => {
          mediaReady = true;
          syncAdaptiveAspectRatio();
          syncAutoplay();
        });
      }

      if ('IntersectionObserver' in window) {
        slideshow._ksFadingSlideshowObserver = new IntersectionObserver((entries) => {
          isVisible = entries.some((entry) => entry.isIntersecting);
          syncAutoplay();
        }, { rootMargin: '100px 0px' });
        slideshow._ksFadingSlideshowObserver.observe(slideshow);
      }

      document.addEventListener('visibilitychange', syncAutoplay);
      reducedMotionMedia.addEventListener('change', syncAutoplay);

      slideshow.addEventListener('shopify:block:select', (event) => {
        const selectedSlide = event.target.closest('[data-fading-slideshow-slide]');
        const selectedIndex = slides.indexOf(selectedSlide);
        if (selectedIndex < 0) return;

        setActive(selectedIndex);
        stopAutoplay();
      });

      slideshow.addEventListener('shopify:block:deselect', syncAutoplay);
    });
  }

  function initMakerProfileBios(scope = document) {
    collect(scope, '[data-maker-profile-bio]').forEach((bio) => {
      if (bio.dataset.makerProfileBioInitialized === 'true') return;

      const richText = bio.querySelector(':scope > .metafield-rich_text_field');
      if (!richText) return;

      const firstQuestion = Array.from(richText.children).find((child) => (
        child.matches('p') && child.querySelector(':scope > strong, :scope > b')
      ));

      if (!firstQuestion) return;

      const qaList = document.createElement('div');
      qaList.className = 'maker-profile__qa-list';
      richText.insertBefore(qaList, firstQuestion);

      let sibling = firstQuestion;
      while (sibling) {
        const nextSibling = sibling.nextSibling;
        qaList.appendChild(sibling);
        sibling = nextSibling;
      }

      bio.dataset.makerProfileBioInitialized = 'true';
    });
  }

  function stripShopifyEditorAttributes(node) {
    if (!(node instanceof Element)) return;

    Array.from(node.attributes).forEach((attribute) => {
      if (attribute.name.startsWith('data-shopify')) {
        node.removeAttribute(attribute.name);
      }
    });

    collect(node, '*').forEach((child) => {
      Array.from(child.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) {
          child.removeAttribute(attribute.name);
        }
      });
    });
  }

  function buildScrollingPromotionLane(viewport) {
    const track = viewport.querySelector('[data-scrolling-promotion-track]');
    const sourceLane = viewport.querySelector('[data-scrolling-promotion-lane]');

    if (!track || !sourceLane) return;

    if (!sourceLane.dataset.originalMarkup) {
      sourceLane.dataset.originalMarkup = sourceLane.innerHTML;
    }

    track.querySelectorAll('[data-scrolling-promotion-clone="true"]').forEach((clone) => clone.remove());
    sourceLane.innerHTML = sourceLane.dataset.originalMarkup;

    const baseItems = Array.from(sourceLane.children);
    if (baseItems.length === 0) return;

    let safety = 0;
    while (sourceLane.scrollWidth < viewport.clientWidth * 1.5 && safety < 12) {
      baseItems.forEach((item) => {
        const clone = item.cloneNode(true);
        stripShopifyEditorAttributes(clone);
        sourceLane.appendChild(clone);
      });

      safety += 1;
    }

    const loopDistance = Math.ceil(sourceLane.getBoundingClientRect().width);
    if (!loopDistance) return;

    const laneClone = sourceLane.cloneNode(true);
    laneClone.dataset.scrollingPromotionClone = 'true';
    laneClone.setAttribute('aria-hidden', 'true');
    stripShopifyEditorAttributes(laneClone);
    track.appendChild(laneClone);
    viewport.style.setProperty('--scrolling-promotion-loop-distance', `${loopDistance}px`);

    collect(sourceLane, 'img').forEach((image) => {
      if (image.complete || image.dataset.scrollingPromotionLoadBound === 'true') return;

      image.dataset.scrollingPromotionLoadBound = 'true';
      image.addEventListener('load', () => buildScrollingPromotionLane(viewport), { once: true });
      image.addEventListener('error', () => buildScrollingPromotionLane(viewport), { once: true });
    });
  }

  function initScrollingPromotions(scope = document) {
    const viewports = collect(scope, '[data-scrolling-promotion]');
    if (viewports.length === 0) return;

    viewports.forEach((viewport) => {
      buildScrollingPromotionLane(viewport);
    });

    if (body.dataset.scrollingPromotionResizeBound !== 'true') {
      body.dataset.scrollingPromotionResizeBound = 'true';

      let resizeFrame = 0;
      const rebuildAll = () => {
        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          collect(document, '[data-scrolling-promotion]').forEach((viewport) => {
            buildScrollingPromotionLane(viewport);
          });
        });
      };

      window.addEventListener('resize', rebuildAll, { passive: true });
      window.addEventListener('load', rebuildAll, { once: true });
    }
  }

  function makeInfiniteGalleryCloneDecorative(node) {
    if (!(node instanceof Element)) return;

    node.setAttribute('aria-hidden', 'true');
    stripShopifyEditorAttributes(node);
    collect(node, 'a, button, input, select, textarea, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    collect(node, 'img').forEach((image) => {
      image.alt = '';
    });
  }

  function waitForInfiniteGalleryImages(container) {
    return Promise.all(collect(container, 'img').map((image) => {
      image.loading = 'eager';

      if (typeof image.decode === 'function') {
        return image.decode().catch(() => undefined);
      }

      if (image.complete) return Promise.resolve();

      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    }));
  }

  async function buildInfiniteImageGallery(gallery) {
    const columnsContainer = gallery.querySelector('[data-infinite-gallery-columns]');
    const source = gallery.querySelector('[data-infinite-gallery-source]');

    if (!columnsContainer || !source) return;

    if (!gallery._ksInfiniteGalleryItems) {
      gallery._ksInfiniteGalleryItems = Array.from(source.querySelectorAll(':scope > [data-infinite-gallery-item]'));
    }

    const items = gallery._ksInfiniteGalleryItems;
    if (items.length === 0) return;

    const buildId = (gallery._ksInfiniteGalleryBuildId || 0) + 1;
    gallery._ksInfiniteGalleryBuildId = buildId;
    gallery.classList.remove('is-ready');
    columnsContainer.replaceChildren();

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const requestedColumns = Number(isMobile ? gallery.dataset.columnsMobile : gallery.dataset.columnsDesktop);
    const columnCount = Math.max(1, Math.min(requestedColumns || 1, items.length));
    const baseDuration = Math.max(1, Number(gallery.dataset.animationDuration) || 40);
    const speedMultipliers = (gallery.dataset.columnSpeeds || '').split(',').map((value) => {
      return Math.max(0.1, (Number(value) || 100) / 100);
    });
    const startingPhases = [0.04, 0.22, 0.38, 0.13, 0.31, 0.47];
    const sequences = [];

    columnsContainer.style.setProperty('--ks-gallery-columns', columnCount);

    for (let index = 0; index < columnCount; index += 1) {
      const column = document.createElement('div');
      const track = document.createElement('div');
      const sequence = document.createElement('div');

      column.className = 'ks-infinite-gallery__column';
      track.className = 'ks-infinite-gallery__track';
      sequence.className = 'ks-infinite-gallery__sequence';
      sequence.dataset.infiniteGallerySequence = 'true';
      const columnDuration = baseDuration / (speedMultipliers[index] || 1);
      track.style.setProperty('--ks-gallery-duration', `${columnDuration}s`);
      track.style.setProperty('--ks-gallery-delay', `${-columnDuration * startingPhases[index]}s`);
      track.appendChild(sequence);
      column.appendChild(track);
      columnsContainer.appendChild(column);
      sequences.push(sequence);
    }

    items.forEach((item, index) => {
      sequences[index % columnCount].appendChild(item);
    });

    await waitForInfiniteGalleryImages(columnsContainer);
    if (gallery._ksInfiniteGalleryBuildId !== buildId || !gallery.isConnected) return;

    const rowGapProperty = isMobile ? '--ks-gallery-row-gap-mobile' : '--ks-gallery-row-gap-desktop';
    const rowGap = parseFloat(getComputedStyle(gallery).getPropertyValue(rowGapProperty)) || 0;
    const minimumSequenceHeight = gallery.clientHeight + rowGap;

    sequences.forEach((sequence) => {
      const originalItems = Array.from(sequence.children);
      let safety = 0;

      while (sequence.getBoundingClientRect().height < minimumSequenceHeight && safety < 12) {
        originalItems.forEach((item) => {
          const filler = item.cloneNode(true);
          makeInfiniteGalleryCloneDecorative(filler);
          sequence.appendChild(filler);
        });
        safety += 1;
      }

      const distance = sequence.getBoundingClientRect().height + rowGap;
      if (!distance) return;

      const duplicate = sequence.cloneNode(true);
      duplicate.dataset.infiniteGalleryDuplicate = 'true';
      makeInfiniteGalleryCloneDecorative(duplicate);
      sequence.parentElement.appendChild(duplicate);
      sequence.parentElement.style.setProperty('--ks-gallery-loop-distance', `${distance}px`);
    });

    window.requestAnimationFrame(() => {
      if (gallery._ksInfiniteGalleryBuildId === buildId) {
        gallery.classList.add('is-ready');
      }
    });
  }

  function initInfiniteImageGalleries(scope = document) {
    collect(scope, '[data-infinite-image-gallery]').forEach((gallery) => {
      if (gallery.dataset.infiniteGalleryInitialized === 'true') return;

      gallery.dataset.infiniteGalleryInitialized = 'true';
      buildInfiniteImageGallery(gallery);

      if ('IntersectionObserver' in window) {
        gallery._ksInfiniteGalleryIntersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            gallery.classList.toggle('is-paused', !entry.isIntersecting);
          });
        }, { rootMargin: '100px 0px' });
        gallery._ksInfiniteGalleryIntersectionObserver.observe(gallery);
      }

      if ('ResizeObserver' in window) {
        let previousWidth = gallery.clientWidth;
        let previousHeight = gallery.clientHeight;
        let resizeFrame = 0;

        gallery._ksInfiniteGalleryResizeObserver = new ResizeObserver(() => {
          const nextWidth = gallery.clientWidth;
          const nextHeight = gallery.clientHeight;
          if (nextWidth === previousWidth && nextHeight === previousHeight) return;

          previousWidth = nextWidth;
          previousHeight = nextHeight;
          window.cancelAnimationFrame(resizeFrame);
          resizeFrame = window.requestAnimationFrame(() => buildInfiniteImageGallery(gallery));
        });
        gallery._ksInfiniteGalleryResizeObserver.observe(gallery);
      }
    });
  }

  function calculateHorizontalScrollAmount(track) {
    const firstItem = track.firstElementChild;
    const trackStyles = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;

    if (!firstItem) {
      return Math.max(track.clientWidth + gap, 0);
    }

    const itemWidth = firstItem.getBoundingClientRect().width;
    const itemStep = itemWidth + gap;

    if (itemStep <= 0) {
      return Math.max(track.clientWidth + gap, 0);
    }

    return itemStep;
  }

  function initHorizontalScrollSections(scope = document) {
    collect(scope, '[data-horizontal-scroll-section]').forEach((section) => {
      const track = section.querySelector('[data-horizontal-scroll-track]');
      const nav = section.querySelector('[data-horizontal-scroll-nav]');
      const prevButton = section.querySelector('[data-horizontal-scroll-prev]');
      const nextButton = section.querySelector('[data-horizontal-scroll-next]');
      const container = section.querySelector('[data-horizontal-scroll-container]')
        || section.querySelector('.theme-container');

      if (!track || !nav || !prevButton || !nextButton) return;

      if (section.dataset.horizontalScrollInitialized === 'true') {
        section.__ksHorizontalScrollUpdate?.();
        return;
      }

      section.dataset.horizontalScrollInitialized = 'true';

      function updateMetrics() {
        if (!container) return;

        const viewportWidth = document.documentElement.clientWidth || window.innerWidth || 0;
        const isContainedTrack = section.hasAttribute('data-horizontal-scroll-contained');
        const trackWidth = isContainedTrack
          ? Math.max(container.getBoundingClientRect().width, 0)
          : Math.max(viewportWidth, 0);

        track.style.setProperty('--ks-horizontal-scroll-track-width', `${trackWidth}px`);

        const edgeOffset = isContainedTrack
          ? 0
          : Math.max(0, container.getBoundingClientRect().left - track.getBoundingClientRect().left);
        track.style.setProperty('--ks-horizontal-scroll-edge', `${edgeOffset}px`);
      }

      function normalizeInitialScrollPosition() {
        if (section.dataset.horizontalScrollNormalized === 'true') return;

        section.dataset.horizontalScrollNormalized = 'true';

        window.requestAnimationFrame(() => {
          const edgeOffset = Number.parseFloat(track.style.getPropertyValue('--ks-horizontal-scroll-edge')) || 0;

          if (Math.abs(track.scrollLeft - edgeOffset) <= 2) {
            track.scrollLeft = 0;
          }

          updateControls();
        });
      }

      function updateControls() {
        updateMetrics();

        const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
        const hasOverflow = maxScrollLeft > 4;
        const supportsCurrentViewport = desktopHorizontalScrollMedia.matches
          || section.hasAttribute('data-horizontal-scroll-mobile-controls');
        const atStart = track.scrollLeft <= 4;
        const atEnd = track.scrollLeft >= maxScrollLeft - 4;

        nav.hidden = !(supportsCurrentViewport && !section.hidden && hasOverflow);
        prevButton.disabled = !hasOverflow || atStart;
        nextButton.disabled = !hasOverflow || atEnd;
      }

      let controlsUpdateFrame = 0;

      function scheduleControlsUpdate() {
        window.cancelAnimationFrame(controlsUpdateFrame);
        controlsUpdateFrame = window.requestAnimationFrame(updateControls);
      }

      function handleArrowClick(direction) {
        const scrollAmount = calculateHorizontalScrollAmount(track);

        track.scrollBy({
          left: direction * scrollAmount,
          behavior: reducedMotionMedia.matches ? 'auto' : 'smooth',
        });
      }

      prevButton.addEventListener('click', () => {
        handleArrowClick(-1);
      });

      nextButton.addEventListener('click', () => {
        handleArrowClick(1);
      });

      track.addEventListener('scroll', scheduleControlsUpdate, { passive: true });

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(scheduleControlsUpdate);

        resizeObserver.observe(track);

        if (container !== track) {
          resizeObserver.observe(container);
        }

        resizeObserver.observe(document.documentElement);
        section.__ksHorizontalScrollResizeObserver = resizeObserver;
      }

      if (typeof MutationObserver === 'function') {
        const mutationObserver = new MutationObserver(() => {
          scheduleControlsUpdate();
        });

        mutationObserver.observe(track, { childList: true });
        section.__ksHorizontalScrollMutationObserver = mutationObserver;
      }

      const handleDesktopMediaChange = () => {
        scheduleControlsUpdate();
      };

      if (typeof desktopHorizontalScrollMedia.addEventListener === 'function') {
        desktopHorizontalScrollMedia.addEventListener('change', handleDesktopMediaChange);
      } else if (typeof desktopHorizontalScrollMedia.addListener === 'function') {
        desktopHorizontalScrollMedia.addListener(handleDesktopMediaChange);
      }

      section.__ksHorizontalScrollUpdate = scheduleControlsUpdate;
      updateControls();
      normalizeInitialScrollPosition();
    });
  }

  function initImageCardTabs(scope = document) {
    collect(scope, '.ks-image-cards').forEach((section) => {
      const panels = collect(section, '[data-image-cards-panel]');
      const tabsContainer = section.querySelector('[data-image-cards-tabs-container]');
      const tablist = section.querySelector('[data-image-cards-tablist]');

      if (panels.length === 0 || !tabsContainer || !tablist) return;

      section.classList.add('ks-image-cards--tabbed');
      section.removeAttribute('data-horizontal-scroll-section');

      const outerRail = section.querySelector('[data-image-cards-outer-rail]');
      const outerTrack = outerRail?.querySelector(':scope > [data-horizontal-scroll-track]');
      outerTrack?.removeAttribute('data-horizontal-scroll-track');

      if (section.dataset.imageCardsTabsInitialized !== 'true') {
        tablist.replaceChildren(...panels.map((panel, panelIndex) => {
          const tab = document.createElement('button');
          const tabId = panel.dataset.imageCardsPanel || String(panelIndex);
          const label = String(panel.dataset.imageCardsLabel || `Category ${panelIndex + 1}`).trim();

          tab.className = 'ks-image-cards__tab';
          tab.id = `ImageCardsTab-${tabId}`;
          tab.type = 'button';
          tab.role = 'tab';
          tab.textContent = label;
          tab.dataset.imageCardsTab = tabId;
          tab.setAttribute('aria-controls', panel.id);
          tab.setAttribute('aria-selected', 'false');
          tab.tabIndex = -1;
          panel.setAttribute('aria-labelledby', tab.id);

          return tab;
        }));
      }

      const tabs = collect(tablist, '[data-image-cards-tab]');
      if (tabs.length === 0) return;

      const setActiveTab = (tabId, { focus = false } = {}) => {
        const activeTab = tabs.find((tab) => tab.dataset.imageCardsTab === tabId) || tabs[0];
        const activeId = activeTab?.dataset.imageCardsTab;

        tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.imageCardsPanel === activeId;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);

          if (isActive) {
            window.requestAnimationFrame(() => {
              panel.__ksHorizontalScrollUpdate?.();
              panel.querySelector('[data-horizontal-scroll-section]')?.__ksHorizontalScrollUpdate?.();
            });
          }
        });

        if (focus) activeTab?.focus();
      };

      if (section.dataset.imageCardsTabsInitialized !== 'true') {
        section.dataset.imageCardsTabsInitialized = 'true';

        tabs.forEach((tab, tabIndex) => {
          tab.addEventListener('click', () => {
            setActiveTab(tab.dataset.imageCardsTab);
          });

          tab.addEventListener('keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

            event.preventDefault();
            let nextIndex = tabIndex;

            if (event.key === 'ArrowLeft') nextIndex = (tabIndex - 1 + tabs.length) % tabs.length;
            if (event.key === 'ArrowRight') nextIndex = (tabIndex + 1) % tabs.length;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = tabs.length - 1;

            setActiveTab(tabs[nextIndex]?.dataset.imageCardsTab, { focus: true });
          });
        });
      }

      setActiveTab(tabs[0]?.dataset.imageCardsTab);
      tabsContainer.hidden = false;
      section.classList.add('is-tabs-initialized');
    });
  }

  function initIconBoxScrollSections(scope = document) {
    collect(scope, '[data-icon-boxes-scroll]').forEach((section) => {
      const track = section.querySelector('[data-icon-boxes-scroll-track]');
      const progress = section.querySelector('[data-icon-boxes-progress]');
      const cards = Array.from(track?.querySelectorAll('.ks-icon-boxes__card') || []);

      if (!track || !progress || cards.length < 2) return;

      if (section.dataset.iconBoxesScrollInitialized === 'true') {
        section.__ksIconBoxesProgressUpdate?.();
        return;
      }

      section.dataset.iconBoxesScrollInitialized = 'true';
      let scrollFrame = null;

      const updateProgress = () => {
        const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
        track.tabIndex = maxScrollLeft > 4 ? 0 : -1;
        const position = maxScrollLeft > 0
          ? Math.max(0, Math.min(cards.length - 1, (track.scrollLeft / maxScrollLeft) * (cards.length - 1)))
          : 0;

        progress.hidden = maxScrollLeft <= 4;
        progress.style.setProperty('--icon-boxes-progress-index', String(position));
        progress.setAttribute('aria-valuenow', String(Math.round(position) + 1));
      };

      section.__ksIconBoxesProgressUpdate = updateProgress;

      track.addEventListener('scroll', () => {
        if (scrollFrame) return;

        scrollFrame = requestAnimationFrame(() => {
          updateProgress();
          scrollFrame = null;
        });
      }, { passive: true });

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(updateProgress);
        resizeObserver.observe(track);
      } else {
        window.addEventListener('resize', updateProgress, { passive: true });
      }

      requestAnimationFrame(updateProgress);
    });
  }

  function initProductMediaZoom(scope = document) {
    collect(scope, '[data-product-media-zoom]').forEach((gallery) => {
      if (gallery.dataset.productMediaZoomInitialized === 'true') return;

      const dialog = gallery.querySelector('[data-product-media-zoom-dialog]');
      const openButtons = collect(gallery, '[data-product-media-zoom-open]');
      const closeButton = dialog?.querySelector('[data-product-media-zoom-close]');
      const zoomTrack = dialog?.querySelector('[data-product-media-zoom-track]');
      const zoomItems = dialog ? collect(dialog, '[data-product-media-zoom-item]') : [];
      const zoomImages = dialog ? collect(dialog, '.ks-product-media__zoom-image') : [];
      const previousButton = dialog?.querySelector('[data-product-media-zoom-prev]');
      const nextButton = dialog?.querySelector('[data-product-media-zoom-next]');
      const currentCount = dialog?.querySelector('[data-product-media-zoom-current]');

      if (!dialog || !zoomTrack || openButtons.length === 0 || typeof dialog.showModal !== 'function') return;

      gallery.dataset.productMediaZoomInitialized = 'true';
      let lastTrigger = null;
      let activeZoomIndex = 0;
      let pendingZoomIndex = null;
      let scrollFrame = null;
      let scrollEndTimer = null;

      const resetModalImageZoom = () => {
        zoomItems.forEach((item) => {
          item.classList.remove('is-zoomed');
          item.style.removeProperty('--zoom-origin-x');
          item.style.removeProperty('--zoom-origin-y');
        });

        zoomImages.forEach((image) => image.setAttribute('aria-pressed', 'false'));
      };

      const toggleModalImageZoom = (image, event = null) => {
        const item = image.closest('[data-product-media-zoom-item]');

        if (!item) return;

        const isZoomed = item.classList.contains('is-zoomed');
        resetModalImageZoom();

        if (isZoomed) return;

        if (event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
          const imageBounds = image.getBoundingClientRect();
          const originX = Math.max(0, Math.min(100, ((event.clientX - imageBounds.left) / imageBounds.width) * 100));
          const originY = Math.max(0, Math.min(100, ((event.clientY - imageBounds.top) / imageBounds.height) * 100));

          item.style.setProperty('--zoom-origin-x', `${originX}%`);
          item.style.setProperty('--zoom-origin-y', `${originY}%`);
        }

        item.classList.add('is-zoomed');
        image.setAttribute('aria-pressed', 'true');
      };

      zoomImages.forEach((image, index) => {
        image.tabIndex = 0;
        image.setAttribute('role', 'button');
        image.setAttribute('aria-label', `Zoom image ${index + 1} further`);
        image.setAttribute('aria-pressed', 'false');

        image.addEventListener('click', (event) => toggleModalImageZoom(image, event));
        image.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;

          event.preventDefault();
          toggleModalImageZoom(image);
        });
      });

      const renderZoomUi = (index) => {
        activeZoomIndex = Math.max(0, Math.min(zoomItems.length - 1, index));

        if (currentCount) currentCount.textContent = String(activeZoomIndex + 1);
        if (previousButton) previousButton.disabled = activeZoomIndex === 0;
        if (nextButton) nextButton.disabled = activeZoomIndex === zoomItems.length - 1;
      };

      const updateZoomUi = ({ force = false } = {}) => {
        if (zoomItems.length === 0) return;

        if (pendingZoomIndex !== null && !force) {
          const pendingItem = zoomItems[pendingZoomIndex];
          const pendingDistance = pendingItem ? Math.abs(pendingItem.offsetLeft - zoomTrack.scrollLeft) : 0;

          if (pendingDistance > 2) {
            renderZoomUi(pendingZoomIndex);
            return;
          }

          pendingZoomIndex = null;
        }

        const closestIndex = zoomItems.reduce((currentClosestIndex, item, itemIndex) => {
          const closestDistance = Math.abs(zoomItems[currentClosestIndex].offsetLeft - zoomTrack.scrollLeft);
          const itemDistance = Math.abs(item.offsetLeft - zoomTrack.scrollLeft);
          return itemDistance < closestDistance ? itemIndex : currentClosestIndex;
        }, 0);

        renderZoomUi(closestIndex);
      };

      const showZoomItem = (index, { smooth = true } = {}) => {
        const nextIndex = Math.max(0, Math.min(zoomItems.length - 1, index));
        const targetItem = zoomItems[nextIndex];

        if (!targetItem) return;

        resetModalImageZoom();

        pendingZoomIndex = nextIndex;
        renderZoomUi(nextIndex);

        zoomTrack.scrollTo({
          left: targetItem.offsetLeft,
          behavior: smooth && !reducedMotionMedia.matches ? 'smooth' : 'auto',
        });

        if (!smooth || reducedMotionMedia.matches) {
          window.requestAnimationFrame(() => {
            pendingZoomIndex = null;
            updateZoomUi({ force: true });
          });
        }
      };

      const releasePageScroll = () => {
        document.documentElement.classList.remove('ks-product-media-zoom-open');
        body.classList.remove('ks-product-media-zoom-open');
      };

      const openZoom = (button) => {
        if (dialog.open) return;

        lastTrigger = button;
        dialog.showModal();
        document.documentElement.classList.add('ks-product-media-zoom-open');
        body.classList.add('ks-product-media-zoom-open');

        requestAnimationFrame(() => {
          const zoomIndex = String(button.dataset.zoomIndex || '0');
          const targetItem = zoomItems.find((item) => String(item.dataset.zoomIndex) === zoomIndex);

          pendingZoomIndex = null;
          zoomTrack.scrollLeft = targetItem ? targetItem.offsetLeft : 0;
          updateZoomUi({ force: true });
          closeButton?.focus({ preventScroll: true });
        });
      };

      openButtons.forEach((button) => {
        button.addEventListener('click', () => openZoom(button));
      });

      closeButton?.addEventListener('click', () => {
        dialog.close();
      });

      previousButton?.addEventListener('click', (event) => {
        event.preventDefault();
        showZoomItem(activeZoomIndex - 1);
      });

      nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        showZoomItem(activeZoomIndex + 1);
      });

      zoomTrack.addEventListener('scroll', () => {
        window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(() => {
          pendingZoomIndex = null;
          updateZoomUi({ force: true });
        }, 140);

        if (scrollFrame) return;

        scrollFrame = requestAnimationFrame(() => {
          updateZoomUi();
          scrollFrame = null;
        });
      }, { passive: true });

      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          showZoomItem(activeZoomIndex - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          showZoomItem(activeZoomIndex + 1);
        }
      });

      dialog.addEventListener('close', () => {
        window.clearTimeout(scrollEndTimer);
        pendingZoomIndex = null;
        resetModalImageZoom();
        releasePageScroll();
        lastTrigger?.focus({ preventScroll: true });
        lastTrigger = null;
      });

      dialog.addEventListener('cancel', releasePageScroll);
    });
  }


    theme.registerFeature('media-features', (scope = document) => {
      initMediaSurfaces(scope);
      initSlideshows(scope);
      initBasicContentFadingSlideshows(scope);
      initMakerProfileBios(scope);
      initScrollingPromotions(scope);
      initInfiniteImageGalleries(scope);
      initIconBoxScrollSections(scope);
      initImageCardTabs(scope);
      initHorizontalScrollSections(scope);
      initProductMediaZoom(scope);
    });
  }

  if (window.KSTheme?.registerFeature) {
    registerMediaFeatures();
  } else {
    document.addEventListener('ks:theme:ready', registerMediaFeatures, { once: true });
  }
})();
